import React from 'react';
import RelativeTime from '../utils/RelativeTime';
import BlogSignOff from './BlogSignOff';
import { PUBLIC_URL } from '../Config';
import './BlogDetailContent.css';

// Same logo banner as the top of the newsletter email
const BLOG_LOGO_URL = 'https://studentscholars.s3.amazonaws.com/email_banner/studentscholars_banner.png';
const BLOG_AUTHOR_IMAGE = `${PUBLIC_URL}/assets/images/hat_purple_1.png`;

const DOCUMENT_TYPES = ['pdf', 'doc', 'docx', 'xlsx'];
const MEDIA_TYPES = ['image', 'video'];

const MediaBox = ({ type, src, alt }) => (
  <div className="blog-detail-media">
    {type === 'video' ? (
      <video controls>
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    ) : (
      <img src={src} alt={alt} />
    )}
  </div>
);

// Shared layout for every blog detail page: logo, purple title bar, author line,
// then each section as title, media, text and link, separated by a line, and the sign-off.
// The blog's cover image/video (`blog_media`) is intentionally not shown here.
export default function BlogDetailContent({ blog }) {
  if (!blog) return null;

  return (
    <div className="blog-detail-page">
      <div className="blog-detail-logo mb-3">
        <img src={BLOG_LOGO_URL} alt="StudentScholars" />
      </div>

      <div className="blog-detail-title mb-3">
        <h2 className="poppins mb-0">{blog.title}</h2>
      </div>

      <div className="blog-detail-line" />

      <div className="blog-detail-line blog-detail-meta d-flex justify-content-between align-items-center py-3">
        <div className="d-flex align-items-center">
          <img src={BLOG_AUTHOR_IMAGE} alt="Admin profile" />
          <span className="ms-3 font-14 fw-bold">{blog.admin_name}</span>
        </div>
        {blog.created_at && (
          <div className="font-14">
            <span>{blog.created_at.split(' ')[0]}</span> • <span className="mx-1"><RelativeTime dateString={blog.created_at} /></span>
          </div>
        )}
      </div>

      {/* Older blogs kept their text on the blog itself; newer ones use description sections */}
      {blog.description && (
        <div dangerouslySetInnerHTML={{ __html: blog.description }} className="blog-detail-text mb-3 mt-2" />
      )}

      {blog.blogdata?.map((item, index) => {
        const documents = (item?.attachments || []).filter(attach => attach?.attachment && DOCUMENT_TYPES.includes(attach.attachment_type));
        const media = (item?.attachments || []).filter(attach => attach?.attachment && MEDIA_TYPES.includes(attach.attachment_type));

        return (
          <div key={index} className="description-block blog-detail-section pb-4 mb-4 mt-4">
            {item?.title && (
              <h3 className="blog-detail-section-title poppins fw-bold mb-3">{item.title}</h3>
            )}

            {media.length > 0 && (
              <div className="row mt-3 justify-content-center">
                {media.map((attach, mIdx) => (
                  <div key={mIdx} className={`${media.length === 1 ? 'col-12' : 'col-md-6'} mb-4`}>
                    <MediaBox type={attach.attachment_type} src={attach.attachment} alt={`Attachment ${mIdx + 1}`} />
                  </div>
                ))}
              </div>
            )}

            {item?.images && (
              <div dangerouslySetInnerHTML={{ __html: item.images }} className="mt-3 mb-3 inline-editor-images" />
            )}

            {item?.description && (
              <div dangerouslySetInnerHTML={{ __html: item.description }} className="blog-detail-text mt-2 mb-3 font-16" />
            )}

            {documents.length > 0 && (
              <div className="mb-3 text-center">
                <span className="fw-bold text-dark d-block mb-2">Attachments:</span>
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {documents.map((attach, aIdx) => (
                    <a
                      key={aIdx}
                      href={attach.attachment}
                      target="_blank"
                      rel="noreferrer"
                      className="badge bg-light text-dark p-2 border d-flex align-items-center gap-2 text-decoration-none shadow-sm"
                      style={{ borderRadius: '8px', cursor: 'pointer' }}
                    >
                      {attach.attachment_type === 'pdf' ? (
                        <span className="badge bg-danger font-11">PDF</span>
                      ) : (
                        <span className="badge bg-secondary font-11">{attach.attachment_type === 'xlsx' ? 'XLSX' : 'DOC'}</span>
                      )}
                      <span className="font-13">{attach.attachment.split('/').pop()}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {item?.link && (
              <div className="text-center mt-3">
                <a
                  href={/^https?:\/\//i.test(item.link) ? item.link : `https://${item.link}`}
                  target="_blank"
                  rel="noreferrer"
                  className="blog-detail-section-link"
                >
                  Learn More
                </a>
              </div>
            )}
          </div>
        );
      })}

      {/* Older blogs have one reference link on the blog itself instead of per section */}
      {blog.link && (
        <div className="blog-detail-link mt-4 mb-4 p-3 rounded text-center">
          <h5 className="fw-bold text-dark mb-2">Reference Link:</h5>
          <a href={blog.link} target="_blank" rel="noreferrer" className="fw-bold text-decoration-underline font-15" style={{ wordBreak: 'break-all' }}>
            {blog.link}
          </a>
        </div>
      )}

      <div className="text-center">
        <BlogSignOff />
      </div>
    </div>
  );
}
